<script setup lang="ts">
/**
 * A labelled block of a record sidebar: a quiet heading with an optional
 * `header` slot at its end (a count, a small button) and the body in a single
 * column below. No frame of its own — the sidebar is one card, and its blocks
 * are set apart by spacing alone.
 *
 * Not `NxPanel` (a framed surface) and not `NxDisclosure` (a toggle): a plain
 * section of a column. The kit version of the ERP's `SidebarSection`, with the
 * same props and slots.
 */
defineProps<{ labelText: string }>();

defineSlots<{
  default?: () => unknown;
  header?: () => unknown;
}>();
</script>

<template>
  <section class="nx-sidebar-section">
    <div class="nx-sidebar-section__head">
      <h3 class="nx-sidebar-section__label">{{ labelText }}</h3>
      <slot name="header" />
    </div>
    <div class="nx-sidebar-section__body">
      <slot />
    </div>
  </section>
</template>

<style scoped>
.nx-sidebar-section {
  width: 100%;
  min-width: 0;
}
.nx-sidebar-section__head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  width: 100%;
  padding-inline: var(--nxt-space-xs);
}
.nx-sidebar-section__label {
  display: flex;
  flex-grow: 1;
  align-items: center;
  margin: 0;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-bold);
}
.nx-sidebar-section__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--nxt-space-md);
  width: 100%;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}
</style>
